import storeConnector from '@store/storeConnector';
import { SiteMeta } from '@src/types/SiteMeta';
import { DataGridProProps, GridRowSpacingParams } from '@mui/x-data-grid-pro';

import useGetDevicesFiltered from '@src/hooks/api/queries/collections/useGetDevicesFiltered';
import useGetInternalWidgetConfig from '@src/hooks/api/queries/useGetInternalWidgetConfig';
import { WidgetTypeDeviceList } from '@src/types/Widget';
import { useCallback, useMemo } from 'react';
import DeviceDataTable from '@src/components/_shared/DeviceDataTable';
import { pcsSx } from '@src/components/_shared/sxStyles/Datatable/deviceTableList';
import UIBox from '@src/components/_shared/UIBox';
import useExpandedRows from '@hooks/useExpandedRows';
import PCSMasterDetail from '@src/components/_shared/PCSContainer/PCSMasterDetail';
import { PCSWidgetProvider } from './PCSWidgetProvider';

interface PCSListProps {
  currentUnitDeviceId: string;
  siteMeta: SiteMeta;
  widgetId?: string;
  isUnitScope?: boolean;
}

function PCSList({
  currentUnitDeviceId,
  siteMeta,
  widgetId,
  isUnitScope,
}: PCSListProps) {
  const { Units } = siteMeta;
  const boxHeader = `${isUnitScope ? siteMeta.UnitNames[siteMeta.Units.indexOf(currentUnitDeviceId)] : ''} PCS List`;
  const {
    data: units,
    isLoading: unitsLoading,
    isError: unitsError,
  } = useGetDevicesFiltered({
    body: {
      filter: {
        type: {
          $in: ['unitController'],
        },
      },
    },
    options: {
      enabled: isUnitScope && !!currentUnitDeviceId,
    },
  });

  const unitControllerId: string = units?.find(
    (unit) => unit.engineeringId === currentUnitDeviceId,
  )?.deviceId as string;

  const {
    data: widgetConfig,
    isLoading,
    isError,
  } = useGetInternalWidgetConfig<WidgetTypeDeviceList>(
    `${widgetId}List`,
    isUnitScope
      ? {
          unitControllerId: unitControllerId || 'unitController_1',
          unitControllerEngineeringId: currentUnitDeviceId || Units[0],
        }
      : {},
    {
      enabled: !!unitControllerId,
      queryKeys: [currentUnitDeviceId],
    },
  );
  const memoizedUnitControllerId = useMemo(
    () => unitControllerId,
    [unitControllerId],
  );
  const memoizedCurrentUnitDeviceId = useMemo(
    () => currentUnitDeviceId,
    [currentUnitDeviceId],
  );

  const getDetailPanelContent = useCallback<
    NonNullable<DataGridProProps['getDetailPanelContent']>
  >(
    ({ row }) => (
      <PCSMasterDetail
        row={row}
        unitControllerId={memoizedUnitControllerId}
        unitEngineeringId={memoizedCurrentUnitDeviceId}
      />
    ),
    [memoizedUnitControllerId, memoizedCurrentUnitDeviceId],
  );

  const getDetailPanelHeight = useCallback(() => 'auto', []);

  const { expandedRowIds } = useExpandedRows(widgetConfig?.devices);

  return (
    <PCSWidgetProvider widgetId={widgetId} isUnitScope={isUnitScope}>
      <UIBox
        header={widgetConfig?.header || boxHeader}
        isError={isError || unitsError}
      >
        <DeviceDataTable
          id='pcs'
          sx={pcsSx.wrapper}
          hideFooter
          devices={widgetConfig?.devices}
          dataPoints={widgetConfig?.dataPoints || []}
          pointConfigRefs={widgetConfig?.pointConfigRefs || []}
          disableRowSelectionOnClick
          getHeaderHeight={() => 0}
          getRowClassName={() => 'MuiDataGrid-clickable pcs-row'}
          getDetailPanelHeight={getDetailPanelHeight}
          getDetailPanelContent={getDetailPanelContent}
          density={'standard'}
          loading={isLoading || unitsLoading}
          initialExpandedDetailPanelIds={expandedRowIds}
          additionalClassnames='pcs-data-table'
          getRowSpacing={(_params: GridRowSpacingParams) => ({
            top: 9,
            bottom: 4,
          })}
        />
      </UIBox>
    </PCSWidgetProvider>
  );
}

export default storeConnector(PCSList, {
  service: ['currentUnitDeviceId'],
  config: ['siteMeta'],
});
